/**
 * Settings → Conference intake. Turns the booth flow on for the tenant,
 * picks which consent form and survey it uses, and manages the forms
 * themselves (add from a template, blank, or a copy of a form here or in
 * another organisation; edit → new version; download a survey's responses;
 * archive; delete a copy).
 */
import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';

import { extractErrorMessage } from '../../api/errors';
import {
  archiveIntakeForm,
  copyIntakeFormFrom,
  createIntakeFormFromTemplate,
  deleteIntakeFormCopy,
  downloadSurveyResponses,
  getIntakeConfig,
  getPostSurveyConfig,
  listIntakeCopySources,
  savePostSurveyConfig,
  type PostSurveyConfig,
  listIntakeForms,
  listIntakeTemplates,
  saveIntakeConfig,
  type IntakeCopySource,
  type IntakeForm,
  type IntakeFormKind,
  type IntakeTemplate,
} from '../../api/intake';
import { SearchableSelect } from '../SearchableSelect';
import { IntakeFormEditor, type EditorTarget } from './IntakeFormEditor';

const KIND_LABEL: Record<IntakeFormKind, string> = {
  consent: 'Consent',
  survey: 'Pre survey',
  or_survey: 'Post survey · OR',
  sim_survey: 'Post survey · Sim',
};
const NO_POST_SURVEY = [{ value: '', label: 'None' }];
/** The capture app's built-in rating, as a post-survey template. */
const SIMPL_TEMPLATE = 'simpl-or-post-survey';
/** Add form, grouped by what the form is for. */
const MENU_GROUPS: {
  label: string;
  kinds: IntakeFormKind[];
  blank: { kind: IntakeFormKind; label: string }[];
}[] = [
  { label: 'Consent', kinds: ['consent'], blank: [{ kind: 'consent', label: 'New consent form' }] },
  { label: 'Pre survey', kinds: ['survey'], blank: [{ kind: 'survey', label: 'New pre survey' }] },
  {
    label: 'Post survey',
    kinds: ['or_survey', 'sim_survey'],
    blank: [
      { kind: 'or_survey', label: 'New post survey (OR) — shown in the capture app' },
      { kind: 'sim_survey', label: 'New post survey (Sim) — from the station QR code' },
    ],
  },
];
const NO_SURVEY = [{ value: '', label: 'No survey' }];

/** Save a blob under `filename` via a temporary download link. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function IntakeSettings({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const [forms, setForms] = useState<IntakeForm[] | null>(null);
  const [templates, setTemplates] = useState<IntakeTemplate[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [consentId, setConsentId] = useState('');
  const [surveyId, setSurveyId] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState<EditorTarget | null>(null);
  const [archiving, setArchiving] = useState<IntakeForm | null>(null);
  const [deleting, setDeleting] = useState<IntakeForm | null>(null);
  // POST-SURVEYS
  const [postCfg, setPostCfg] = useState<PostSurveyConfig | null>(null);
  const [orId, setOrId] = useState('');
  const [simId, setSimId] = useState('');
  const [copySources, setCopySources] = useState<IntakeCopySource[] | null>(null);
  const [copyTenant, setCopyTenant] = useState('');
  const [copyForm, setCopyForm] = useState('');

  const loadForms = useCallback(async () => {
    setForms(await listIntakeForms(tenantId));
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [f, t, c] = await Promise.all([
          listIntakeForms(tenantId),
          listIntakeTemplates(tenantId),
          getIntakeConfig(tenantId),
        ]);
        if (cancelled) return;
        setForms(f);
        setTemplates(t);
        setEnabled(c.enabled);
        setConsentId(c.consent_family_id ?? '');
        setSurveyId(c.survey_family_id ?? '');
        try {
          const p = await getPostSurveyConfig(tenantId);
          if (!cancelled) {
            setPostCfg(p);
            setOrId(p.or_family_id ?? '');
            setSimId(p.sim_family_id ?? '');
          }
        } catch {
          /* an older server without post surveys: the card stays hidden */
        }
      } catch (e) {
        if (!cancelled) {
          setError(extractErrorMessage(e, 'could not load conference intake settings'));
          setForms([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(extractErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const onSaveConfig = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const c = await saveIntakeConfig(tenantId, {
        enabled,
        consent_family_id: consentId || null,
        survey_family_id: surveyId || null,
      });
      setEnabled(c.enabled);
      setConsentId(c.consent_family_id ?? '');
      setSurveyId(c.survey_family_id ?? '');
      setNotice('Conference intake settings saved.');
    } catch (e) {
      setError(extractErrorMessage(e, 'could not save the intake settings'));
    } finally {
      setSaving(false);
    }
  };

  const fromTemplate = (t: IntakeTemplate) => {
    setMenuAnchor(null);
    void run(async () => {
      const f = await createIntakeFormFromTemplate(tenantId, t.key);
      await loadForms();
      setNotice(`Added “${f.name}”.`);
    }, 'could not add the form');
  };

  // A copy in this organisation opens straight in the editor, since a copy
  // is almost always made to be changed.
  const onDuplicate = (f: IntakeForm) =>
    void run(async () => {
      const copy = await copyIntakeFormFrom(tenantId, tenantId, f.family_id);
      await loadForms();
      setEditing(copy);
    }, 'could not copy the form');

  const openCopyFrom = () => {
    setMenuAnchor(null);
    setCopyTenant('');
    setCopyForm('');
    void run(async () => {
      setCopySources(await listIntakeCopySources(tenantId));
    }, 'could not load the other organisations’ forms');
  };

  const onCopyFrom = () =>
    void run(async () => {
      const f = await copyIntakeFormFrom(tenantId, copyTenant, copyForm);
      setCopySources(null);
      await loadForms();
      setNotice(`Copied “${f.name}”. Pick it under Consent form or Survey to use it.`);
    }, 'could not copy the form');

  const onDelete = (f: IntakeForm) =>
    void run(async () => {
      try {
        await deleteIntakeFormCopy(tenantId, f.family_id);
      } finally {
        setDeleting(null);
      }
      await loadForms();
      setNotice(`Deleted “${f.name}”.`);
    }, 'could not delete the form');

  const onSavePostSurveys = () =>
    void run(async () => {
      const p = await savePostSurveyConfig(tenantId, {
        or_family_id: orId || null,
        sim_family_id: simId || null,
      });
      setPostCfg(p);
      setNotice('Post survey settings saved.');
    }, 'could not save the post survey settings');

  const onDownload = (f: IntakeForm) =>
    void run(async () => {
      const blob = await downloadSurveyResponses(tenantId, f.family_id);
      saveBlob(blob, `${f.name}-responses.csv`);
    }, 'could not download the responses');

  const onArchive = (f: IntakeForm) =>
    void run(async () => {
      await archiveIntakeForm(tenantId, f.family_id);
      setArchiving(null);
      if (consentId === f.family_id) setConsentId('');
      if (surveyId === f.family_id) setSurveyId('');
      await loadForms();
    }, 'could not archive the form');

  if (forms === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const option = (f: IntakeForm) => ({ value: f.family_id, label: `${f.name} (v${f.version})` });
  const consentOptions = forms.filter((f) => f.kind === 'consent').map(option);
  const surveyOptions = forms.filter((f) => f.kind === 'survey').map(option);
  const orOptions = forms.filter((f) => f.kind === 'or_survey').map(option);
  const simOptions = forms.filter((f) => f.kind === 'sim_survey').map(option);

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Conference intake
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Enroll people at a conference booth: photograph their badge, confirm
            the details it reads, then hand the tablet over for the study consent
            form and the survey. Finishing creates the participant with their
            signed consent and answers attached.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Switch
                checked={enabled}
                disabled={!canWrite || saving}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Enroll participants with conference intake"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <SearchableSelect
              label="Consent form"
              value={consentId}
              onChange={setConsentId}
              options={consentOptions}
              disabled={!canWrite || saving}
              fullWidth
            />
            <SearchableSelect
              label="Pre survey"
              value={surveyId}
              onChange={setSurveyId}
              options={surveyOptions}
              pinned={NO_SURVEY}
              disabled={!canWrite || saving}
              fullWidth
            />
          </Stack>
          {canWrite && (
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => void onSaveConfig()} disabled={saving}>
                {saving ? 'Saving…' : 'Save intake settings'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {postCfg && (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Post surveys
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Create post surveys under <strong>Forms → Add form</strong>. The{' '}
              <strong>OR</strong> one is shown in the capture app after each recording
              (without one, the app asks its built-in three questions). The{' '}
              <strong>Sim</strong> one is answered on the participant’s own phone: after a
              simulation they scan the QR code from <strong>Participants → Post survey</strong>,
              pick the recording and answer. Phones that enrolled at the booth are recognised; others identify
              with their email or badge code.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <SearchableSelect
                label="Post survey (OR)"
                value={orId}
                onChange={setOrId}
                options={orOptions}
                pinned={NO_POST_SURVEY}
                disabled={!canWrite || busy}
                fullWidth
              />
              <SearchableSelect
                label="Post survey (Sim)"
                value={simId}
                onChange={setSimId}
                options={simOptions}
                pinned={NO_POST_SURVEY}
                disabled={!canWrite || busy}
                fullWidth
              />
            </Stack>
            {canWrite && (
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={onSavePostSurveys} disabled={busy}>
                  Save post surveys
                </Button>
              </Box>
            )}

          </CardContent>
        </Card>
      )}


      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" color="text.secondary">
              Forms
            </Typography>
            {canWrite && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                disabled={busy}
              >
                Add form
              </Button>
            )}
          </Stack>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {MENU_GROUPS.flatMap((g) => [
              <ListSubheader key={`h-${g.label}`}>{g.label}</ListSubheader>,
              ...g.blank.map((b) => (
                <MenuItem
                  key={`b-${b.kind}`}
                  onClick={() => {
                    setMenuAnchor(null);
                    setEditing({ kind: b.kind, blank: true });
                  }}
                >
                  {b.label}
                </MenuItem>
              )),
              ...templates
                .filter((t) => g.kinds.includes(t.kind))
                .map((t) => (
                  <MenuItem key={t.key} onClick={() => fromTemplate(t)}>
                    {t.key === SIMPL_TEMPLATE
                      ? 'Post survey (OR): copy of the capture app’s current one'
                      : `From template: ${t.name}`}
                  </MenuItem>
                )),
            ])}
            <Divider />
            <MenuItem onClick={openCopyFrom}>Copy from another organisation…</MenuItem>
          </Menu>
          <Divider sx={{ my: 1 }} />
          {forms.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No forms yet.{canWrite && ' Use Add form to start from a template or a blank form.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kind</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((f) => (
                  <TableRow key={f.family_id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={KIND_LABEL[f.kind]}
                        color={f.kind === 'consent' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {f.name}
                      {f.is_copy && (
                        <Chip size="small" label="Copy" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>v{f.version}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={canWrite ? 'Edit' : 'View'}>
                        <IconButton
                          size="small"
                          aria-label={`Edit ${f.name}`}
                          onClick={() => setEditing(f)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canWrite && (
                        <Tooltip title="Make a copy">
                          <IconButton
                            size="small"
                            aria-label={`Make a copy of ${f.name}`}
                            onClick={() => onDuplicate(f)}
                            disabled={busy}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {f.kind !== 'consent' && (
                        <Tooltip title="Download responses">
                          <IconButton
                            size="small"
                            aria-label={`Download responses to ${f.name}`}
                            onClick={() => onDownload(f)}
                            disabled={busy}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWrite && f.is_copy && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${f.name}`}
                            onClick={() => setDeleting(f)}
                            disabled={busy}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWrite && (
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            aria-label={`Archive ${f.name}`}
                            onClick={() => setArchiving(f)}
                            disabled={busy}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <IntakeFormEditor
          open
          tenantId={tenantId}
          form={editing}
          readOnly={!canWrite}
          onClose={() => setEditing(null)}
          onSaved={(f) => {
            setEditing(null);
            setNotice(`Saved “${f.name}” (v${f.version}).`);
            void loadForms().catch((e) =>
              setError(extractErrorMessage(e, 'could not reload the forms')),
            );
          }}
        />
      )}

      <Dialog
        open={copySources !== null}
        onClose={() => !busy && setCopySources(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Copy a form from another organisation</DialogTitle>
        <DialogContent>
          {copySources !== null && copySources.length === 0 ? (
            <DialogContentText>
              None of the other organisations you run has a consent form or survey
              yet.
            </DialogContentText>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                The copy is a new form in this organisation, starting at version 1.
                Changing it later doesn’t change the original, and no responses
                are copied.
              </DialogContentText>
              <SearchableSelect
                label="Organisation"
                value={copyTenant}
                onChange={(v) => {
                  setCopyTenant(v);
                  setCopyForm('');
                }}
                options={(copySources ?? []).map((s) => ({
                  value: s.tenant_id,
                  label: s.tenant_name,
                }))}
                fullWidth
              />
              <SearchableSelect
                label="Form"
                value={copyForm}
                onChange={setCopyForm}
                options={(copySources ?? [])
                  .find((s) => s.tenant_id === copyTenant)
                  ?.forms.map((f) => ({
                    value: f.family_id,
                    label: `${KIND_LABEL[f.kind]}: ${f.name} (v${f.version})`,
                  })) ?? []}
                disabled={!copyTenant}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopySources(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onCopyFrom}
            disabled={busy || !copyTenant || !copyForm}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => !busy && setDeleting(null)}>
        <DialogTitle>Delete this copy?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{deleting?.name}” and all its versions are deleted for good. The
            form it was copied from isn’t affected. A copy that intake uses, or
            that someone has already signed or answered, can’t be deleted —
            archive it instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleting && onDelete(deleting)}
            disabled={busy}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!archiving} onClose={() => !busy && setArchiving(null)}>
        <DialogTitle>Archive form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{archiving?.name}” will no longer be offered for intake. Responses
            already collected with it are kept and still show on each participant.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiving(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => archiving && onArchive(archiving)}
            disabled={busy}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
